// @erp/module-cms/schemas — THE READING GATE (ADR-010 §2.12; decided with the owner
// 2026-09-20, built 2026-09-21). Browser-safe: zod + plain data only — the admin Settings
// screen, the publication form, the gateway and the member portal's reader all parse the
// SAME rule, so what an editor is told will open is exactly what the server opens.
//
// ┌──────────────────────────────────────────────────────────────────────────────────┐
// │ Reading a publication is FREE but REQUIRES AN ACCOUNT: the website stops offering   │
// │ downloads and sends readers to the member portal's in-browser reader; the first     │
// │ visit creates the account (email + password, a one-time code — no Google).          │
// │                                                                                      │
// │ • `access_level` on cms_publication grows one value: public | MEMBERS | private.     │
// │   The STORED value is the editor's choice; the EFFECTIVE value applies the wall.     │
// │ • The wall is a SETTING (`reading.openAfterMonths`), default null = everything       │
// │   gated; the owner opens older issues later from Settings without a deploy. A        │
// │   `members` issue whose published_at is older than the wall reads as `public`;       │
// │   `private` (the PIN) is never opened by the wall.                                   │
// │ • Downloads are OFF by default (`reading.allowDownload`) — the reader shows the       │
// │   document inline; a Download button appears only when the setting allows it AND     │
// │   the effective level is public. Site-wide, never per record.                        │
// │ • A publication may point at its copy in the library (`library_ebook_id`) — an        │
// │   opaque LIB id the CMS never joins; the admin shell fills the picker only when the   │
// │   install runs `lib`. The public wire names the OPAC eBooks shelf for it.             │
// │                                                                                      │
// │ HONEST LIMIT: a presigned inline URL is savable by a determined reader. This is a    │
// │ funnel (an account per reader), not DRM — no watermarking, no view-only rendering.    │
// └──────────────────────────────────────────────────────────────────────────────────┘
import { z } from "zod";

// ── The publication access-level vocabulary — public | members | private ────────────────
// DISTINCT from RecordAccessLevel (private.ts: public | private, the tenders / events /
// titles dimension): only a publication has a FILE a member can read, so only it carries
// the middle value. `private` keeps its 0011 meaning (a PIN unlocks the record).
export const PUBLICATION_ACCESS_LEVELS = ["public", "members", "private"] as const;
export const PublicationAccessLevel = z.enum(PUBLICATION_ACCESS_LEVELS);
export type PublicationAccessLevel = z.infer<typeof PublicationAccessLevel>;

/** The two levels an editor may choose as the DEFAULT for a new publication (`private` needs a PIN — never a default). */
export const READING_DEFAULT_ACCESS_LEVELS = ["public", "members"] as const;
export const ReadingDefaultAccess = z.enum(READING_DEFAULT_ACCESS_LEVELS);
export type ReadingDefaultAccess = z.infer<typeof ReadingDefaultAccess>;

// ── The `reading` setting — cms_setting key 'reading' (seeded by db-cms 0024) ───────────

/** The cms_setting key the reading configuration lives under. */
export const READING_SETTING_KEY = "reading";

/** The longest wall the setting accepts — 100 years, in months; a bound, not a meaning. */
export const READING_OPEN_AFTER_MONTHS_MAX = 1200;

/**
 * The structured value behind the `reading` setting (the `locales` 0020 precedent: a typed
 * shape stored as one JSON string in the key/value store, read and written through the
 * existing settings routes).
 *   • defaultAccess   — what the publication form starts a NEW publication on;
 *   • openAfterMonths — THE MOVING WALL: a `members` publication published more than this
 *                       many months ago (30-day months) reads as public; null = never;
 *   • allowDownload   — whether the reader offers a Download button for an effectively
 *                       public publication (site-wide; a members-gated one never downloads).
 */
export const ReadingSettings = z.object({
  defaultAccess: ReadingDefaultAccess,
  openAfterMonths: z.number().int().min(0).max(READING_OPEN_AFTER_MONTHS_MAX).nullable(),
  allowDownload: z.boolean(),
});
export type ReadingSettings = z.infer<typeof ReadingSettings>;

/**
 * The product default (the owner, 2026-09-20): reading requires an account, nothing opens
 * on its own, downloads are off. What 0024 seeds, what DEFAULT_SETTINGS carries, and what a
 * missing or malformed row reads as — never a 500.
 */
export const READING_SETTINGS_DEFAULT: ReadingSettings = { defaultAccess: "members", openAfterMonths: null, allowDownload: false };

/** The stored form — canonical key order so the value is byte-stable across writers. */
export const serializeReadingSettings = (r: ReadingSettings): string =>
  JSON.stringify({ defaultAccess: r.defaultAccess, openAfterMonths: r.openAfterMonths, allowDownload: r.allowDownload });

/**
 * The stored string → the typed settings, DEFAULTING to READING_SETTINGS_DEFAULT when the
 * row is missing, malformed or fails the schema (the *_json safe-degradation law: a bad
 * row must never 500 every public feed request). Only schema-validated JSON is ever
 * written (ReadingSettingValue below), so the fallback is for a hand-edited row.
 */
export const parseReadingSettings = (raw: string | null | undefined): ReadingSettings => {
  if (!raw) return READING_SETTINGS_DEFAULT;
  try {
    const parsed = ReadingSettings.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : READING_SETTINGS_DEFAULT;
  } catch {
    return READING_SETTINGS_DEFAULT;
  }
};

/**
 * The settings-store validator for the `reading` key (the LocalesSettingValue precedent):
 * the JSON string a PATCH sends, parsed through ReadingSettings and RE-SERIALISED canonical
 * — a stray key, a negative wall or a non-boolean download flag is a validation error; the
 * stored value is always the canonical form. A string in, a string out: the settings wire
 * stays `Record<key, string>`.
 */
export const ReadingSettingValue = z
  .string()
  .max(2000)
  .transform((value, ctx) => {
    let raw: unknown;
    try {
      raw = JSON.parse(value);
    } catch {
      ctx.addIssue({ code: "custom", message: "reading must be a JSON object { defaultAccess, openAfterMonths, allowDownload }" });
      return z.NEVER;
    }
    const parsed = ReadingSettings.strict().safeParse(raw);
    if (!parsed.success) {
      ctx.addIssue({ code: "custom", message: parsed.error.issues[0]?.message ?? "reading must be { defaultAccess, openAfterMonths, allowDownload }" });
      return z.NEVER;
    }
    return serializeReadingSettings(parsed.data);
  });

// ── The wall, as ONE rule both the SQL gate and the projection read ─────────────────────

/** A "month" of the wall — 30 days in ms (the setting says months; the rule says 30-day months, so a wall is a fixed instant, never calendar arithmetic). */
export const READING_WALL_MONTH_MS = 30 * 86_400_000;

/**
 * THE CUTOFF: the instant before which a `members` publication's published_at makes it
 * effectively public — `now − openAfterMonths·30d`, or 0 when the wall is off (null): no
 * epoch-ms published_at is < 0, so a 0 cutoff matches nothing, in SQL and in TypeScript
 * alike. The media-keys gate, the reconciler and the serve gate bind this value; the
 * projection compares against it — one number, one rule.
 */
export const readingCutoffMs = (reading: Pick<ReadingSettings, "openAfterMonths">, now: number): number =>
  reading.openAfterMonths === null ? 0 : Math.max(0, now - reading.openAfterMonths * READING_WALL_MONTH_MS);

/**
 * THE EFFECTIVE LEVEL: `members` published before the cutoff → `public`; everything else is
 * the stored value. `private` is never opened by the wall (a PIN is a promise to a person,
 * not a date); a members DRAFT (published_at null) stays members.
 */
export const effectiveAccessLevel = (
  level: PublicationAccessLevel,
  publishedAt: number | null,
  reading: Pick<ReadingSettings, "openAfterMonths">,
  now: number,
): PublicationAccessLevel =>
  level === "members" && publishedAt !== null && publishedAt < readingCutoffMs(reading, now) ? "public" : level;

// ── The public wire: `readAccess` on every public publication DTO ───────────────────────

/**
 * What the site needs to render the CTA for one publication (ADR-010 §2.12), on every
 * public publication DTO — list and detail, both locales:
 *   • level      — the EFFECTIVE level (the wall applied);
 *   • download   — settings.allowDownload && level === "public" && a pdf exists;
 *   • readUrl    — the member portal's reader (`<MEMBER_PORTAL_URL>/read/<slug>[?locale=]`),
 *                  null when there is no pdf or the level is private;
 *   • libraryUrl — the OPAC eBooks shelf, non-null only when `library_ebook_id` is set (LIB
 *                  has no ebook detail route yet; the id is stored for the day it does).
 */
export const PublicationReadAccess = z.object({
  level: PublicationAccessLevel,
  download: z.boolean(),
  readUrl: z.string().url().nullable(),
  libraryUrl: z.string().url().nullable(),
});
export type PublicationReadAccess = z.infer<typeof PublicationReadAccess>;

/** An origin-ish var with any trailing slashes removed — `https://erp-student.ssneri.com/` and the bare form mint the same URLs. */
const trimBase = (base: string): string => base.replace(/\/+$/, "");

/**
 * The member portal's reader URL for a publication: `<base>/read/<slug>`, plus `?locale=`
 * ONLY when the row's locale is not the install's default (the site's own unprefixed-default
 * convention — a Hindi issue carries `?locale=hi`, an English one nothing).
 */
export const readingUrlFor = (memberPortalUrl: string, slug: string, locale: string, defaultLocale: string): string =>
  `${trimBase(memberPortalUrl)}/read/${encodeURIComponent(slug)}${locale === defaultLocale ? "" : `?locale=${encodeURIComponent(locale)}`}`;

/** The OPAC eBooks shelf — the one library URL the wire names today (no per-ebook route exists yet). */
export const libraryEbooksUrlFor = (memberPortalUrl: string): string => `${trimBase(memberPortalUrl)}/catalogue/ebooks`;

// ── The reader endpoint — GET /api/cms/reading/:slug ─────────────────────────────────────

/** The reader's presigned GET lifetime — 15 minutes (a long read refetches on expiresAt; the serve route's 5-minute default is for a click). */
export const READING_GET_EXPIRES_S = 15 * 60;

/** The file the reader shows — a presigned GET (inline for a PDF, attachment otherwise), its type, its size and when the URL dies. */
export const ReadingFile = z.object({
  url: z.string().url(),
  contentType: z.string().min(1),
  bytes: z.number().int().nonnegative(),
  expiresAt: z.number().int().nonnegative(),
});
export type ReadingFile = z.infer<typeof ReadingFile>;
